import { useEffect, useRef, useState } from 'react';
import ndjsonStream from 'can-ndjson-stream';
import _ from 'lodash';
import * as amplitude from '@amplitude/analytics-browser';
import { SUMMARIZER_URI } from './globals';

export default function useSummary(election, query, party, exact) {
    const [summary, setSummary] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [data, setData] = useState(null);
    const [areFactsGrounded, setAreFactsGrounded] = useState(null);
    const [error, setError] = useState(null);
    const [errorMsg, setErrorMsg] = useState(null);
    const ndJSONStreamReader = useRef(null);

    const stream = async () => {
        await ndJSONStreamReader.current?.cancel();
        setGenerating(false);
        const body = JSON.stringify({
            query,
            party_uuid: party?.uuid,
            election_uuid: election?.uuid,
            exact
        });
        const response = await fetch(`${SUMMARIZER_URI}/statement`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body
        });
        if (!response.ok) {
            // If we've errored, don't start listening on the stream and just exit after
            // setting an error message
            if (response.status === 503) {
                setErrorMsg(
                    'Op dit moment is de samenvattings capaciteit van OpenVerkiezingen volledig bezet. ' +
                        'Hierdoor kunnen we geen samenvatting voor je maken.'
                );
            } else {
                setErrorMsg('Op dit moment kunnen we geen samenvatting voor je maken.');
            }
            return;
        }

        ndJSONStreamReader.current = ndjsonStream(response.body).getReader();

        let result;
        setGenerating(true);
        while (!result || !result.done) {
            result = await ndJSONStreamReader.current.read();
            if (result?.value?.type === 'error') {
                // Not all errors will be http errors, cause some might occur during the stream.
                // Here we catch those
                setError(result?.value);
            } else if (result?.value?.type === 'answer') {
                const content = _.cloneDeep(result?.value?.content);
                setSummary(prev => (prev || '') + content);
            } else if (result?.value?.type === 'context') {
                if (result?.value?.name === 'Rag[Paragraphs]') {
                    const content = _.cloneDeep(result?.value?.content);
                    setData(prev => ({ ...prev, paragraphs: content }));
                } else if (result?.value?.name === 'Rag[FactsGrounded]') {
                    const content = _.cloneDeep(result?.value?.content);
                    setAreFactsGrounded(content);
                }
            }
        }

        setGenerating(false);
    };

    useEffect(() => {
        if (party && query) {
            setSummary(null);
            setErrorMsg(null);
            setError(null);
            setData(null);

            stream();

            amplitude.track('Summary requested', { exact, election });
        }
    }, [party, query, election, exact]);

    useEffect(() => {
        if (data) {
            amplitude.track(
                'Summary viewed',
                // { generating: !data?.summary, approximate: !!data?.approximate },
                { generating, approximate: !!data?.approximate }
            );
        }
    }, [data]);

    useEffect(() => {
        if (error) {
            if (error?.status === 429) {
                setErrorMsg(
                    'Dat zijn iets te veel verzoeken in een korte tijd! ' +
                        'Probeer het over een paar seconden nog eens.'
                );
            } else if (error?.status === 503) {
                setErrorMsg(
                    'Momenteel is alle capaciteit van OpenVerkiezingen.nl volledig bezet. ' +
                        'Probeer het over enige tijd nog eens. Je kunt wel een van de standaard stellingen ' +
                        'boven het vrije invoerveld proberen.'
                );
            } else if (error?.status === 404) {
                setErrorMsg(
                    'Het lijkt erop dat we voor deze verkiezingen nog geen programma van deze ' +
                        'partij in onze database hebben.'
                );
            } else {
                setErrorMsg(
                    'Sorry, er ging iets mis. Mogelijk hebben we nog geen programma ' +
                        'van deze partij voor deze verkiezingen.'
                );
            }
        } else {
            setErrorMsg(null);
        }
    }, [error]);

    const pending = !summary && !errorMsg;
    return {
        summary,
        generating,
        data,
        areFactsGrounded,
        errorMsg,
        pending
    };
}
